<template>
  <div>
    <PageHeader
      :title="pageTitle"
      :description="pageDescription"
      v-if="showAddSurveyButton"
      :button="{
        icon: 'fas fa-plus-circle',
        text: `Ajouter un sondage`,
        action: createGroup
      }"
    />
    <PageHeader
      v-else
      :title="pageTitle"
      :description="pageDescription"
    />
    <SurveysSubmenu />

    <PageContent>

      <MVPTable
        :gridData="surveyGroups"
        :options="gridOptions"
        @rowDoubleClicked="handleRowDoubleClick"
        :showAllDetails="false"
      />
    </PageContent>
  </div>
</template>

<script>
import { http } from '@/services/api.service'
import OneGroup from './Modals/OneGroup'
import PageHeader from '@/components/PageHeader.vue'
import PageContent from '@/components/PageContent.vue'
import MVPTable from '@/components/agGrid/MVPTable.vue'
import SurveysSubmenu from './SurveysSubmenu.vue'
import { mapGetters } from 'vuex'
import MultiListRowDetail from './MultiListRowDetail.vue'

export default {
  name: 'app-surveys-multilist',
  components: {
    PageHeader,
    PageContent,
    MVPTable,
    SurveysSubmenu,
    MultiListRowDetail // eslint-disable-line
  },
  data () {
    return {
      surveyGroups: [],
      loading: false
    }
  },
  computed: {
    ...mapGetters([
      'roles',
      'user',
      'domain',
      'application'
    ]),

    showAddSurveyButton () {
      return this.$route.path !== '/app/surveys/media-mvp'
    },

    isMediaMvpDomain () {
      return this.domain && (this.domain.slug === '0-media-interne' || this.domain.id === 2)
    },

    pageTitle () {
      const statusParam = this.$route.query.status || 'active'
      return statusParam === 'active' ? 'Sondages' : 'Sondages inactifs'
    },

    pageDescription () {
      const statusParam = this.$route.query.status || 'active'
      return statusParam === 'active' ? 'Sondages' : 'Sondages inactifs'
    },

    isAdmin () {
      return this.roles.includes('admin')
    },
    hasEditionRights () {
      return this.roles.some(role => ['admin', 'director', 'community-manager'].includes(role))
    },

    gridOptions () {
      const isMediaMvpTab = this.$route.path === '/app/surveys/media-mvp'
      const isMediaMvpDomain = this.domain.slug === '0-media-interne' || this.domain.id === 2

      let columnDefs = [
        { cellRenderer: 'agGroupCellRenderer', width: 10, suppressMenu: true },
        {
          headerName: 'Titre',
          field: 'group_title',
          valueGetter: params => {
            const surveys = params.data.surveys || []
            if (surveys.length === 1) {
              return surveys[0].question
            }
            return params.data.group_title
          }
        },
        { headerName: 'Date début', field: 'start_at', type: ['dateColumnFilterable'], cellRenderer: this.surveyDatesCellRenderer },
        { headerName: 'Date fin', field: 'end_at', type: ['dateColumnFilterable'], cellRenderer: this.surveyDatesCellRenderer },
        {
          headerName: 'Statut',
          width: 80,
          field: 'is_notified',
          cellRenderer: 'TagCellRenderer',
          cellRendererParams: {
            tag: {
              labels: {
                true: 'Notifié',
                false: 'En attente'
              }
            }
          },
          filter: 'agSetColumnFilter',
          filterParams: {
            cellRenderer: 'TagFilterCellRenderer',
            cellRendererParams: {
              tag: {
                labels: {
                  true: 'Notifié',
                  false: 'En attente'
                }
              },
              buttons: ['reset']
            }
          },
          sortable: true,
          hide: isMediaMvpDomain
        }
      ]

      // Add Duplications count column for media-mvp domain (like News tab)
      if (isMediaMvpDomain) {
        columnDefs.push({
          headerName: 'Duplications',
          type: 'numericColumn',
          field: 'duplications_count',
          filter: 'agNumberColumnFilter',
          width: 110,
          sortable: true,
          valueGetter: params => {
            const surveys = Array.isArray(params?.data?.surveys) ? params.data.surveys : []
            if (!surveys.length) return 0
            // Use the maximum duplications_count among the group's surveys as the group duplication count
            return surveys.reduce((max, s) => {
              const n = Number(s?.duplications_count)
              return Number.isFinite(n) && n > max ? n : max
            }, 0)
          }
        })
      }

      // Add duplication button for media-mvp domain (only for active surveys)
      if (isMediaMvpTab) {
        columnDefs.push({
          headerName: 'Action',
          field: 'button',
          cellRenderer: params => {
            // Only show duplicate button for active surveys on media-mvp
            if (params.data.is_enabled) {
              return `<button class="button is-small is-primary duplicate-btn" data-uuid="${params.data.uuid}">Dupliquer</button>`
            }
            return ''
          },
          width: 120,
          sortable: false,
          hide: !this.hasEditionRights
        })
      }

      columnDefs.push({
        headerName: 'Stats CSV',
        field: 'uuid',
        cellRenderer: params => {
          if (params.value) {
            const isSingle = params.data && params.data.is_single ? 'true' : 'false'
            return `<a href="#" class="download-stats" data-uuid="${params.value}" data-single="${isSingle}">Stats</a>`
          } else return ''
        },
        width: 200,
        sortable: false,
        hide: !(this.isAdmin || this.roles.some(role => ['community-manager', 'director'].includes(role)) || (this.application.app.slug === 'tract' && this.roles.includes('director')))
      })

      return {
        masterDetail: true,
        detailCellRenderer: 'MultiListRowDetail',
        detailRowAutoHeight: true,
        suppressContextMenu: true,
        detailCellRendererParams: {
          detailGridOptions: {
            columnDefs: this.getSurveyColumnDefs(),
            defaultColDef: {
              flex: 1,
              editable: true,
              resizable: true
            },
            onGridReady (params) {
              params.api.setDomLayout('autoHeight')
            }
          },
          getDetailRowData (params) {
            params.successCallback(params.data.surveys)
          }
        },
        columnDefs
      }
    }
  },
  watch: {
    '$route.query.status': {
      handler (newStatus, oldStatus) {
        this.loadSurveysGroups()
      }
    },
    '$route.query.refreshToken' (newToken, oldToken) {
      if (newToken !== oldToken) {
        this.loadSurveysGroups()
      }
    }
  },
  methods: {
    refreshAfterChange (targetStatus, currentStatus) {
      const refreshToken = Date.now().toString()

      if (targetStatus !== currentStatus) {
        this.$router.push({
          path: '/app/multisurveys',
          query: { status: targetStatus, refreshToken }
        }).catch(() => {
          this.loadSurveysGroups()
        })
        return
      }

      const nextQuery = {
        ...this.$route.query,
        refreshToken
      }

      if (this.$route.query.refreshToken !== refreshToken) {
        this.$router.replace({
          path: this.$route.path,
          query: nextQuery
        }).catch(() => {
          this.loadSurveysGroups()
        })
      } else {
        this.loadSurveysGroups()
      }
    },
    loadSurveysGroups () {
      this.loading = true
      const statusParam = this.$route.query.status || 'active'
      const isMavieproTab = this.$route.path === '/app/surveys/media-mvp'

      let apiUrl = '/domain/surveys?past&multiple'
      let params = {}

      if (isMavieproTab) {
        params.domain_id = 2
      }

      http.get(apiUrl, { params }).then(response => {
        let filteredGroups = response.data

        const now = new Date()
        const isSingleActive = (item) => {
          if (!item || !item.is_single) return false
          if (!item.start_at || !item.end_at) return false
          const start = new Date(item.start_at)
          const end = new Date(item.end_at)
          return start <= now && now <= end
        }
        const isSinglePast = (item) => {
          if (!item || !item.is_single) return false
          if (!item.end_at) return false
          const end = new Date(item.end_at)
          return end < now
        }

        if (statusParam === 'active') {
          filteredGroups = filteredGroups.filter(group => {
            // For legacy single surveys, Active if date window contains now
            if (group.is_single) return isSingleActive(group)
            // For grouped surveys, keep existing behavior (is_enabled)
            return group.is_enabled
          })
        } else if (statusParam === 'inactive') {
          filteredGroups = filteredGroups.filter(group => {
            // For legacy single surveys, Inactive if passed
            if (group.is_single) return isSinglePast(group)
            // For grouped surveys, keep existing behavior (not enabled)
            return !group.is_enabled
          })
        }

        this.surveyGroups = filteredGroups
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },
    getSurveyColumnDefs () {
      return [
        { headerName: 'Question', field: 'question' }
      ]
    },
    handleRowDoubleClick ({ data }) {
      // Legacy single surveys are displayed as synthetic one-item groups.
      // Editing them via the group modal isn't supported; ignore double-click.
      if (data && data.is_single) return
      if (!this.hasEditionRights) return
      this.$buefy.modal.open({
        parent: this,
        component: OneGroup,
        hasModalCard: true,
        customClass: 'content-editor-modal-host',
        width: 960,
        canCancel: ['escape', 'outside'],
        destroyOnHide: false,
        ariaRole: 'dialog',
        ariaModal: true,
        props: {
          editGroup: data
        },
        events: {
          updated: (updatedGroupData) => {
            let isEnabled = false
            if (updatedGroupData?.group?.is_enabled !== undefined) {
              isEnabled = updatedGroupData.group.is_enabled
            } else if (updatedGroupData?.is_enabled !== undefined) {
              isEnabled = updatedGroupData.is_enabled
            }
            const targetStatus = isEnabled ? 'active' : 'inactive'
            const currentStatus = this.$route.query.status || 'active'
            this.refreshAfterChange(targetStatus, currentStatus)
            this.$buefy.notification.open({
              duration: 4000,
              message: 'Le sondage a été mis à jour avec succès !',
              position: 'is-bottom-right',
              type: 'is-success',
              hasIcon: true
            })
          },
          deleted: () => {
            this.loadSurveysGroups()
            this.$buefy.notification.open({
              duration: 4000,
              message: 'Le sondage a été supprimé avec succès !',
              position: 'is-bottom-left',
              type: 'is-success',
              hasIcon: true
            })
          }
        }
      })
    },
    createGroup () {
      this.$buefy.modal.open({
        parent: this,
        component: OneGroup,
        hasModalCard: true,
        customClass: 'content-editor-modal-host',
        width: 960,
        canCancel: ['escape', 'outside'],
        destroyOnHide: false,
        ariaRole: 'dialog',
        ariaModal: true,
        events: {
          created: (createdGroupData) => {
            let isEnabled = false
            if (createdGroupData?.group?.is_enabled !== undefined) {
              isEnabled = createdGroupData.group.is_enabled
            } else if (createdGroupData?.is_enabled !== undefined) {
              isEnabled = createdGroupData.is_enabled
            }

            const targetStatus = isEnabled ? 'active' : 'inactive'
            const currentStatus = this.$route.query.status || 'active'

            this.refreshAfterChange(targetStatus, currentStatus)

            this.$buefy.notification.open({
              duration: 4000,
              message: 'Le sondage a été créé avec succès !',
              position: 'is-bottom-left',
              type: 'is-success',
              hasIcon: true
            })
          }
        }
      })
    },
    surveyDatesCellRenderer (params) {
      if (params.value === null || params.value === undefined) {
        return 'À définir'
      } else {
        const date = new Date(params.value)
        return new Intl.DateTimeFormat('fr-FR', {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric'
        }).format(date)
      }
    },
    // getRowHeight (params) {
    //   if (params.node && params.node.detail) {
    //     const heightPerSurvey = 19
    //     const heightPerOption = 22

    //     const allSurveysOptionsHeight = params.data.surveys.reduce((totalHeight, survey) => {
    //       const surveyHeight = (survey.options.length * heightPerOption) + heightPerSurvey
    //       return totalHeight + surveyHeight
    //     }, 0)

    //     const gridSizes = params.api.getSizesForCurrentTheme()

    //     return allSurveysOptionsHeight + gridSizes.headerHeight
    //   }
    //   return null
    // },
    handleDownloadClick (event) {
      const target = event.target
      if (target && target.classList.contains('download-stats')) {
        event.preventDefault()

        const uuid = target.getAttribute('data-uuid')
        const isSingle = target.getAttribute('data-single') === 'true'
        if (!uuid) return

        this.downloadStats(uuid, isSingle)
      }
    },
    async downloadStats (uuid, isSingle) {
      try {
        const endpoint = isSingle ? `/surveys/${uuid}/stats` : `/surveys/${uuid}/groupstats`
        const response = await http.get(endpoint, { responseType: 'blob' })
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `stats_${uuid}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        this.$buefy.notification.open({
          duration: 3000,
          message: 'Erreur lors du téléchargement des statistiques',
          type: 'is-danger'
        })
      }
    }
  },

  created () {
    this.loadSurveysGroups()
  },

  mounted () {
    this.$nextTick(() => {
      document.addEventListener('click', this.handleDownloadClick)
    })
  },

  beforeDestroy () {
    document.removeEventListener('click', this.handleDownloadClick)
  }

}
</script>
